import { useLocation } from 'react-router-dom'
import {
  useCachedUserProfileState,
  userProfileApi,
} from 'state/PublicWebApi/UserProfile'
import { Box, Button, Link, Paper, Typography } from '@mui/material'
import Info from '@mui/icons-material/Info'
import { useSliceStateSelector } from 'slices/hooks'
import { useTranslation } from 'react-i18next'

const SETTINGS_URLS = {
  klient: '/mitt-trr/kontoinstallningar',
  kund: '/trr-foretag/mina-foretag',
}

const RequestContactUpdate = () => {
  const { t } = useTranslation()
  const { mobilePhone, email, outdatedContact } = useCachedUserProfileState()

  const [trigger, result] =
    userProfileApi.endpoints.confirmUserProfileContactInformation.useLazyQuery()
  const [updateUser] = userProfileApi.endpoints.getUserProfile.useLazyQuery()
  const location = useLocation()
  const { idp } = useSliceStateSelector((state) => state.slices.auth.profile)
  const settingsLink = SETTINGS_URLS[idp as 'klient' | 'kund']
  const isOutdated = outdatedContact ?? false
  const error = result?.isError
  const link: string = settingsLink

  const confirmHandler = () => {
    void (async () => {
      await trigger()
      await updateUser()
    })()
  }

  const disabled = result?.isFetching || result?.isLoading

  if (
    !isOutdated ||
    location.pathname.includes(settingsLink) ||
    !settingsLink ||
    result?.isSuccess
  ) {
    return null
  }

  return (
    <Paper
      elevation={0}
      sx={{
        my: 3,
        backgroundColor: 'info.light',
        display: 'flex',
        p: 2,
        flexWrap: 'wrap',
      }}
    >
      <Box pr={2}>
        <Info color="info" />
      </Box>
      <Box flex={1}>
        <Typography variant="subtitle1" component="h2" fontWeight={700} mb={2}>
          {/* Type assertion needed until strict:true or strictNullChecks:true is set for TS */}
          {t('translation:requestContactUpdate.title') as string}
        </Typography>

        <Typography mb={2} variant="body1">
          {/* Type assertion needed until strict:true or strictNullChecks:true is set for TS */}
          {t('translation:requestContactUpdate.description') as string}
        </Typography>
        <Box display="flex" flexWrap="wrap">
          <Typography variant="subtitle1" mr={1} component="span">
            {/* Type assertion needed until strict:true or strictNullChecks:true is set for TS */}
            {t('translation:requestContactUpdate.phone') as string}
          </Typography>
          <Typography variant="body1">{mobilePhone}</Typography>
        </Box>
        <Box display="flex" flexWrap="wrap">
          <Typography variant="subtitle1" mr={1} component="span">
            {/* Type assertion needed until strict:true or strictNullChecks:true is set for TS */}
            {t('translation:requestContactUpdate.email') as string}
          </Typography>
          <Typography variant="body1">{email}</Typography>
        </Box>
        {error && (
          <Box display="flex" flexWrap="wrap" pt={2}>
            <Typography variant="body2" color="error">
              {/* Type assertion needed until strict:true or strictNullChecks:true is set for TS */}
              {t('translation:requestContactUpdate.errorMessage') as string}
            </Typography>
          </Box>
        )}
        <Box
          pt={2}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
        >
          <Link href={link} underline="hover">
            {/* Type assertion needed until strict:true or strictNullChecks:true is set for TS */}
            {t('translation:requestContactUpdate.changeInfoLink') as string}
          </Link>
          <Button disabled={disabled} onClick={confirmHandler}>
            {/* Type assertion needed until strict:true or strictNullChecks:true is set for TS */}
            {t('translation:requestContactUpdate.confirmButton') as string}
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}

export default RequestContactUpdate
